import ClearIcon from '@mui/icons-material/Clear';
import {Box, CircularProgress, IconButton, Stack, Typography} from '@mui/material';
import {FC, memo, useCallback, useContext, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {IWorkerSelectOption, searchWorkersStrategy} from '../../../../api/strategies/searchWorkersStrategy';
import {useSearchStrategy} from '../../../../hooks/useSearchStrategy';
import ControlledInput from '../../../../ui/controlled-input/ControlledInput';

import {find} from 'lodash';
import {useRecoilState, useRecoilStateLoadable, useSetRecoilState} from 'recoil';
import {EventBusContext} from '../../../../providers/EventBus';
import {innerListStyle} from '../../../../shared/styles';
import {IUserAccount} from '../../../../shared/types/user-account.types';
import {IAssignTo, assignToState} from '../../../../store/service-requests/atoms';
import {accountGroupsQueryTrigger} from '../../../../store/user-groups/atoms';
import {accountGroupsQuery} from '../../../../store/user-groups/selectors';
import {WorkerRow} from './WorkerRow';
import {BASE_TITLES} from '../../../../shared/constants';

const WorkersAndGroupsBase: FC = () => {
  const [{state, contents}, setAccountGroups] = useRecoilStateLoadable(accountGroupsQuery);
  const {control, setValue, watch} = useForm<{search: string; wasSearching: boolean}>({
    defaultValues: {
      wasSearching: false,
    },
  });
  const {handleSearch, data, searchLoading, searchTerm} = useSearchStrategy<IWorkerSelectOption<IUserAccount>>(
    searchWorkersStrategy,
    true,
  );
  const [assignTo, setAssignTo] = useRecoilState(assignToState);
  const triggerGroupsQuery = useSetRecoilState(accountGroupsQueryTrigger);
  const {bus} = useContext(EventBusContext);

  const wasSearchingWatch = watch('wasSearching');

  const chooseAssignTo = useCallback((assignTo: IAssignTo) => {
    setAssignTo((prev) => {
      if (find(prev, {id: assignTo.id})) return prev.filter((i) => i.id !== assignTo.id);
      return [...prev, assignTo];
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (state === 'hasValue') setAccountGroups(contents);

    if (state === 'hasError')
      bus?.emit('error', [
        contents,
        {
          toastId: 'userGroups',
        },
      ]);

    return () => {
      if (state !== 'loading') triggerGroupsQuery((v) => v + 1);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const assignToUser = assignTo.filter((i) => i.type === 'USER');

  return (
    <>
      <Stack direction="row">
        <ControlledInput
          control={control}
          name="search"
          size="small"
          onBlur={() => {
            setValue('wasSearching', false);
          }}
          search={(str) => {
            handleSearch(str);
            if (!wasSearchingWatch) setValue('wasSearching', true);
          }}
          label="Поиск"
          fullWidth
          placeholder={BASE_TITLES.workers.plural}
          InputProps={{
            endAdornment: searchTerm ? (
              <IconButton
                size="small"
                onClick={() => {
                  setValue('search', '', {shouldDirty: true});
                  handleSearch('');
                }}
              >
                <ClearIcon color="error" />
              </IconButton>
            ) : (
              <></>
            ),
          }}
        />
      </Stack>
      <Box sx={{mt: 3, ...innerListStyle, height: 'calc(100% - 48px - 24px - 30.75px - 16px)'}}>
        {state === 'loading' || searchLoading ? (
          <Stack
            direction="column"
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              overflowY: 'auto',
              padding: '3px',
            }}
          >
            <CircularProgress size={30} sx={{mt: 1}} />
          </Stack>
        ) : !data.length && !searchLoading && wasSearchingWatch ? (
          <Stack
            direction="column"
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              overflowY: 'auto',
              padding: '3px',
            }}
          >
            <Typography>Ничего не найдено</Typography>
          </Stack>
        ) : (
          <Stack
            direction="column"
            sx={{
              height: '100%',
              overflowY: 'auto',
              padding: '3px',
            }}
          >
            {/* TODO Раскомментировать когда понадобится */}
            {/* {!!contents.content.length && (
              <WorkerGroups groups={contents.content} assignTo={assignTo} chooseAssignTo={chooseAssignTo} />
            )} */}
            {!!data.length &&
              data.map((i) => (
                <WorkerRow
                  key={i.value.uid}
                  choose={chooseAssignTo}
                  isChosen={!!find(assignToUser, {id: i.value.uid})}
                  worker={i}
                />
              ))}
          </Stack>
        )}
      </Box>
    </>
  );
};

export const WorkersAndGroups = memo(WorkersAndGroupsBase);
